<script module>
  import AppLayout from '../../layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script>
  import { router } from '@inertiajs/svelte'
  import { Button } from '$lib/components/ui/button/index.js'
  import { Input } from '$lib/components/ui/input/index.js'
  import { Card, CardContent, CardHeader, CardAction } from '$lib/components/ui/card/index.js'
  import PageContainer from '$lib/components/page-container.svelte'
  import DateRangePicker from '$lib/components/date-range-picker.svelte'
  import PlusIcon from '@lucide/svelte/icons/plus'
  import TrashIcon from '@lucide/svelte/icons/trash-2'

  let { property, room, errors = {} } = $props()

  // @svelte-ignore state_referenced_locally
  let roomName = $state(room.name)
  // @svelte-ignore state_referenced_locally
  let entries = $state(
    room.income_entries.map(e => ({
      id: e.id,
      tenant_name: e.tenant_name ?? '',
      monthly_rent_rm: e.monthly_rent / 100,
      start_date: e.start_date ?? '',
      end_date: e.end_date ?? '',
      _destroy: false
    }))
  )
  let submitting = $state(false)

  let visibleEntries = $derived(entries.filter(e => !e._destroy))

  function addEntry() {
    entries = [...entries, { id: null, tenant_name: '', monthly_rent_rm: '', start_date: '', end_date: '', _destroy: false }]
  }

  function removeEntry(i) {
    if (entries[i].id) {
      entries = entries.map((e, idx) => idx === i ? { ...e, _destroy: true } : e)
    } else {
      entries = entries.filter((_, idx) => idx !== i)
    }
  }

  function deleteRoom() {
    if (!confirm(`Delete "${room.name}"? This cannot be undone.`)) return
    router.delete(`/properties/${property.id}/rooms/${room.id}`)
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (submitting) return
    submitting = true

    const income_entries_attributes = entries.reduce((acc, entry, i) => {
      const data = {
        tenant_name: entry.tenant_name,
        monthly_rent: Math.round(parseFloat(entry.monthly_rent_rm || '0') * 100),
        start_date: entry.start_date,
        end_date: entry.end_date,
        _destroy: entry._destroy ? '1' : '0'
      }
      if (entry.id) data.id = entry.id
      acc[i] = data
      return acc
    }, {})

    router.patch(`/properties/${property.id}/rooms/${room.id}`, {
      room: { name: roomName, income_entries_attributes }
    }, { onFinish: () => { submitting = false } })
  }
</script>

<svelte:head>
  <title>Edit Room — MyRentalTax</title>
</svelte:head>

<PageContainer>
  {#snippet breadcrumb()}
    <span class="text-[11px] font-medium text-muted-foreground">Income Entry · Step 1 of 6</span>
  {/snippet}

  <div class="flex flex-col gap-1">
    <h1 class="text-xl sm:text-2xl font-semibold text-foreground m-0">Edit Room</h1>
    <p class="text-sm text-muted-foreground m-0">{property.name}</p>
  </div>

  <form id="room-form" onsubmit={handleSubmit} class="flex flex-col gap-4">
    {#if errors.name}
      <div class="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-600">{errors.name[0]}</div>
    {/if}
    {#if errors.base}
      <div class="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-600">{errors.base[0]}</div>
    {/if}
    {#if Object.keys(errors).some(k => k.startsWith('income_entries'))}
      <div class="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-600">One or more income entries are invalid — please check monthly rent and tenancy dates.</div>
    {/if}

    <!-- Room name -->
    <Card class="shadow-none border-border">
      <CardContent class="px-5 py-5 sm:px-6">
        <div class="flex flex-col gap-1.5">
          <label for="room-name" class="text-sm font-medium text-foreground">Room Name</label>
          <Input id="room-name" type="text" placeholder="e.g. Master Bedroom" bind:value={roomName} required class="h-9" />
        </div>
      </CardContent>
    </Card>

    <!-- Income entries (only non-destroyed) -->
    {#each entries as entry, i (i)}
      {#if !entry._destroy}
        {@const visibleIndex = visibleEntries.indexOf(entry)}
        <Card class="shadow-none border-border">
          <CardHeader class="px-5 py-4">
            <span class="text-sm font-semibold text-foreground">Income Entry {visibleIndex + 1}</span>
            {#if visibleEntries.length > 1}
              <CardAction>
                <button
                  type="button"
                  class="p-1 rounded text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  onclick={() => removeEntry(i)}
                  aria-label="Remove entry"
                >
                  <TrashIcon class="w-4 h-4" />
                </button>
              </CardAction>
            {/if}
          </CardHeader>
          <CardContent class="px-5 pb-5 sm:px-6 border-t border-border pt-4 flex flex-col gap-4">
            <div class="flex flex-col gap-1.5">
              <label for="tenant-name-{i}" class="text-sm font-medium text-foreground">Tenant Name</label>
              <Input id="tenant-name-{i}" type="text" placeholder="e.g. Ahmad bin Ali" bind:value={entry.tenant_name} class="h-9" />
            </div>
            <div class="flex flex-col gap-1.5">
              <label for="monthly-rent-{i}" class="text-sm font-medium text-foreground">Monthly Rent (RM)</label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">RM</span>
                <Input id="monthly-rent-{i}" type="number" placeholder="1200" bind:value={entry.monthly_rent_rm} min="0" step="0.01" class="h-9 pl-9" />
              </div>
            </div>
            <DateRangePicker bind:startDate={entry.start_date} bind:endDate={entry.end_date} />
          </CardContent>
        </Card>
      {/if}
    {/each}

    <Button type="button" variant="outline" onclick={addEntry} class="w-full">
      <PlusIcon class="w-4 h-4" />
      Add Another Income Entry
    </Button>
  </form>

  {#snippet footer()}
    <Button variant="destructive" type="button" onclick={deleteRoom}>Delete Room</Button>
    <div class="flex-1"></div>
    <Button type="submit" form="room-form" disabled={submitting}>
      {submitting ? 'Saving…' : 'Save Changes'}
    </Button>
  {/snippet}
</PageContainer>
